"use client";

import { c as _c } from "react/compiler-runtime";
import { CaretDown, Check, MagnifyingGlass } from "@phosphor-icons/react";
import { useLocale } from "next-intl";
import { useEffect, useId, useState } from "react";
import { useListingTransition } from "@/components/storefront/ListingTransitionContext";
import { RangeSlider } from "@/components/storefront/ui/range-slider";
import { useCurrency } from "@/lib/currency/CurrencyContext";
import { formatPrice } from "@/lib/currency/formatPrice";
import { matchesQuery } from "@/lib/storefront/products/searchMatch";
import { hasVariantOptions, toggleVariantOption, type VariantOptionSelection, writeVariantOptions } from "@/lib/storefront/products/listFilterParams";
import type { FacetBrandDTO, FacetCategoryDTO, FacetColorDTO, FacetsDTO, FacetTagDTO } from "@/lib/storefront/products/listService";
type AppliedFilters = {
  brandSlugs: string[];
  colors: string[]; // English values
  tagSlugs: string[];
  categorySlugs: string[];
  variantOptions: VariantOptionSelection;
  minPrice: number | null;
  maxPrice: number | null;
  inStockOnly: boolean;
};
type Props = {
  facets: FacetsDTO;
  applied: AppliedFilters;
  /**
   * When true, drop the outer card chrome and skip the duplicate header.
   * The mobile filter Drawer renders its own title row + close button, so
   * embedding the standalone card-with-header inside it doubles the
   * "الفلاتر" heading and wraps the sections in a redundant inner card.
   */
  embedded?: boolean;
  /** All-products page: the category tiles and chips replace the tree. */
  hideCategoryTree?: boolean;
};
const COLLAPSED_COUNT = 6;
export default function ProductFilters({
  facets,
  applied,
  embedded = false,
  hideCategoryTree = false
}: Props) {
  const locale = useLocale();
  const isRTL = locale === "ar";
  const {
    navigateWithParams
  } = useListingTransition();
  const updateParam = (mutate: (params: URLSearchParams) => void) => navigateWithParams(params => {
    mutate(params);
    params.delete("page");
  });
  const toggleBrand = (slug: string) => updateParam(p => {
    const next = applied.brandSlugs.includes(slug) ? applied.brandSlugs.filter(b => b !== slug) : [...applied.brandSlugs, slug];
    if (next.length === 0) p.delete("brand");else p.set("brand", next.join(","));
  });
  const toggleColor = (valueEn: string) => updateParam(p_0 => {
    const next_0 = applied.colors.includes(valueEn) ? applied.colors.filter(c => c !== valueEn) : [...applied.colors, valueEn];
    if (next_0.length === 0) p_0.delete("color");else p_0.set("color", next_0.join(","));
  });
  const toggleTag = (slug_0: string) => updateParam(p_1 => {
    const next_1 = applied.tagSlugs.includes(slug_0) ? applied.tagSlugs.filter(t => t !== slug_0) : [...applied.tagSlugs, slug_0];
    if (next_1.length === 0) p_1.delete("tag");else p_1.set("tag", next_1.join(","));
  });
  const toggleCategory = (slug_1: string) => updateParam(p_2 => {
    const next_2 = applied.categorySlugs.includes(slug_1) ? applied.categorySlugs.filter(c_0 => c_0 !== slug_1) : [...applied.categorySlugs, slug_1];
    if (next_2.length === 0) p_2.delete("category");else p_2.set("category", next_2.join(","));
  });
  const toggleOption = (group: string, value: string) => updateParam(p_3 => writeVariantOptions(p_3, toggleVariantOption(applied.variantOptions, group, value)));
  const setPrice = (min: number | null, max: number | null) => updateParam(p_4 => {
    if (min === null) p_4.delete("min_price");else p_4.set("min_price", String(min));
    if (max === null) p_4.delete("max_price");else p_4.set("max_price", String(max));
  });
  const toggleInStock = () => updateParam(p_5 => {
    if (applied.inStockOnly) p_5.delete("in_stock");else p_5.set("in_stock", "true");
  });
  const clearAll = () => updateParam(p_6 => {
    p_6.delete("brand");
    p_6.delete("color");
    p_6.delete("tag");
    p_6.delete("category");
    writeVariantOptions(p_6, {});
    p_6.delete("min_price");
    p_6.delete("max_price");
    p_6.delete("in_stock");
  });

  // With the tree hidden the panel has no category control, so a category
  // alone must not surface Clear All (which still clears it with the rest).
  const anyApplied = applied.brandSlugs.length > 0 || applied.colors.length > 0 || applied.tagSlugs.length > 0 || !hideCategoryTree && applied.categorySlugs.length > 0 || hasVariantOptions(applied.variantOptions) || applied.minPrice !== null || applied.maxPrice !== null || applied.inStockOnly;
  const hasBrandFacet = facets.brands.length > 0;
  const hasColorFacet = facets.colors.length > 0;
  const hasTagFacet = facets.tags.length > 0;
  const hasCategoryFacet = !hideCategoryTree && facets.categories.length > 0;
  const hasPriceFacet = !!facets.priceRange && facets.priceRange.max > facets.priceRange.min;

  // The first two sections that actually render start open — Categories and
  // Brand are conditional, so this counts rendered sections rather than source
  // order. A section holding an applied filter always starts open too, so an
  // active choice is never hidden behind a collapsed header.
  let sectionIndex = 0;
  const openByDefault = (hasApplied: boolean) => sectionIndex++ < 2 || hasApplied;
  return <aside className={embedded ? "divide-y divide-border" : "rounded-2xl border border-border bg-background divide-y divide-border"}>
      <FilterHeader anyApplied={anyApplied} onClearAll={clearAll} isRTL={isRTL} embedded={embedded} />

      {hasCategoryFacet && <FilterSection key="categories" title={isRTL ? "الأقسام" : "Categories"} defaultOpen={openByDefault(applied.categorySlugs.length > 0)}>
          <CategoryTree categories={facets.categories} selected={applied.categorySlugs} onToggle={toggleCategory} isRTL={isRTL} moreLabel={isRTL ? "المزيد" : "more"} lessLabel={isRTL ? "أقل" : "less"} />
        </FilterSection>}

      {hasBrandFacet && <FilterSection key="brand" title={isRTL ? "العلامة التجارية" : "Brand"} defaultOpen={openByDefault(applied.brandSlugs.length > 0)}>
          <CheckboxList items={facets.brands.map(b_0 => ({
        key: b_0.slug,
        label: b_0.name
      }))} selected={applied.brandSlugs} onToggle={toggleBrand} isRTL={isRTL} moreLabel={isRTL ? "المزيد" : "more"} lessLabel={isRTL ? "أقل" : "less"} />
        </FilterSection>}

      <FilterSection key="availability" title={isRTL ? "التوفر" : "Availability"} defaultOpen={openByDefault(applied.inStockOnly)}>
        <InStockCheckbox active={applied.inStockOnly} onToggle={toggleInStock} isRTL={isRTL} />
      </FilterSection>

      {hasPriceFacet && <FilterSection key="price" title={isRTL ? "السعر" : "Price Range"} defaultOpen={openByDefault(applied.minPrice !== null || applied.maxPrice !== null)}>
          <PriceRange range={facets.priceRange!} minPrice={applied.minPrice} maxPrice={applied.maxPrice} onApply={setPrice} isRTL={isRTL} />
        </FilterSection>}

      {hasColorFacet && <FilterSection key="color" title={isRTL ? "اللون" : "Color"} defaultOpen={openByDefault(applied.colors.length > 0)}>
          <ColorSwatchGrid colors={facets.colors} selected={applied.colors} onToggle={toggleColor} isRTL={isRTL} moreLabel={isRTL ? "المزيد" : "more"} lessLabel={isRTL ? "أقل" : "less"} />
        </FilterSection>}
      {/* NB: ColorSwatchGrid emits value_en when present (with value_ar fallback)
          so URLs stay readable. */}

      {facets.optionGroups.map(group_0 => <FilterSection key={group_0.key} title={group_0.label} defaultOpen={openByDefault((applied.variantOptions[group_0.key] ?? []).length > 0)}>
          <CheckboxList items={group_0.values.map(v => ({
        key: v.value,
        label: v.label
      }))} selected={applied.variantOptions[group_0.key] ?? []} onToggle={value_0 => toggleOption(group_0.key, value_0)} isRTL={isRTL} moreLabel={isRTL ? "المزيد" : "more"} lessLabel={isRTL ? "أقل" : "less"} />
        </FilterSection>)}

      {hasTagFacet && <FilterSection key="tags" title={isRTL ? "الوسوم" : "Tags"} defaultOpen={openByDefault(applied.tagSlugs.length > 0)}>
          <CheckboxList items={facets.tags.map(t_0 => ({
        key: t_0.slug,
        label: t_0.name
      }))} selected={applied.tagSlugs} onToggle={toggleTag} isRTL={isRTL} moreLabel={isRTL ? "المزيد" : "more"} lessLabel={isRTL ? "أقل" : "less"} />
        </FilterSection>}
    </aside>;
}

// ─── Category tree (any depth) ──────────────────────────────────────

/** A facet category with its descendants, as the shopper sees them. */
type CategoryNode = {
  category: FacetCategoryDTO;
  children: CategoryNode[];
};

/**
 * Builds the facet's parent → child links into a real tree of any depth.
 *
 * The previous version kept a single `roots` list plus one children map and
 * promoted anything whose parent wasn't a root, which silently flattened every
 * category three or more levels deep — its parent is a child, never a root.
 *
 * A category whose parent is genuinely absent from the facet is still promoted
 * to top level rather than hidden. A parent that only groups its children
 * arrives with `selectable: false` and keeps its place in the shape.
 */
function buildCategoryTree(categories: FacetCategoryDTO[]): CategoryNode[] {
  const bySlug = new Map<string, CategoryNode>(categories.map(c => [c.slug, {
    category: c,
    children: []
  }]));
  const roots: CategoryNode[] = [];
  for (const c of categories) {
    const node = bySlug.get(c.slug);
    if (!node) continue;
    const parent = c.parentSlug ? bySlug.get(c.parentSlug) : undefined;
    if (parent && parent !== node) parent.children.push(node);else roots.push(node);
  }
  return roots;
}

/**
 * Prunes the tree to a search. A node that matches keeps its whole subtree —
 * the shopper asked for that branch — otherwise it survives only as the path
 * to a descendant that matched.
 */
function searchCategoryTree(nodes: CategoryNode[], query: string): CategoryNode[] {
  const kept: CategoryNode[] = [];
  for (const node of nodes) {
    if (matchesQuery(node.category.name, query)) {
      kept.push(node);
      continue;
    }
    const children = searchCategoryTree(node.children, query);
    if (children.length > 0) kept.push({
      category: node.category,
      children
    });
  }
  return kept;
}

/** Whether anything in this subtree is currently ticked. */
function hasSelectedDescendant(node: CategoryNode, selected: string[]): boolean {
  return node.children.some(child => selected.includes(child.category.slug) || hasSelectedDescendant(child, selected));
}
function CategoryTree(t0) {
  const $ = _c(33);
  const {
    categories,
    selected,
    onToggle,
    moreLabel,
    lessLabel,
    isRTL
  } = t0;
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const [opened, setOpened] = useState(_temp);
  let t1;
  if ($[0] !== categories.length) {
    t1 = isSearchable(categories.length);
    $[0] = categories.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const searchable = t1;
  let t2;
  if ($[2] !== query || $[3] !== searchable) {
    t2 = searchable && query.trim() !== "";
    $[2] = query;
    $[3] = searchable;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const searching = t2;
  let hidden;
  let overflows;
  let t3;
  let t4;
  if ($[5] !== categories || $[6] !== expanded || $[7] !== isRTL || $[8] !== onToggle || $[9] !== opened || $[10] !== query || $[11] !== searchable || $[12] !== searching || $[13] !== selected) {
    const tree = buildCategoryTree(categories);
    const roots = searching ? searchCategoryTree(tree, query) : tree;
    overflows = !searching && roots.length > COLLAPSED_COUNT;
    const visible = expanded || !overflows ? roots : roots.slice(0, COLLAPSED_COUNT);
    hidden = overflows ? roots.length - COLLAPSED_COUNT : 0;
    let t5;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = function toggleBranch(slug) {
        setOpened(prev => {
          const next = new Set(prev);
          if (next.has(slug)) {
            next.delete(slug);
          } else {
            next.add(slug);
          }
          return next;
        });
      };
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    const toggleBranch = t5;
    if ($[19] !== isRTL || $[20] !== query || $[21] !== searchable) {
      t3 = searchable && <FilterSearch value={query} onChange={setQuery} isRTL={isRTL} />;
      $[19] = isRTL;
      $[20] = query;
      $[21] = searchable;
      $[22] = t3;
    } else {
      t3 = $[22];
    }
    t4 = roots.length === 0 ? <NoMatches isRTL={isRTL} /> : <ul className="flex flex-col gap-0.5">{visible.map(node => <CategoryBranch key={node.category.slug} node={node} selected={selected} onToggle={onToggle} opened={opened} onToggleBranch={toggleBranch} searching={searching} isRTL={isRTL} />)}</ul>;
    $[5] = categories;
    $[6] = expanded;
    $[7] = isRTL;
    $[8] = onToggle;
    $[9] = opened;
    $[10] = query;
    $[11] = searchable;
    $[12] = searching;
    $[13] = selected;
    $[14] = hidden;
    $[15] = overflows;
    $[16] = t3;
    $[17] = t4;
  } else {
    hidden = $[14];
    overflows = $[15];
    t3 = $[16];
    t4 = $[17];
  }
  let t5;
  if ($[23] !== expanded || $[24] !== hidden || $[25] !== lessLabel || $[26] !== moreLabel || $[27] !== overflows) {
    t5 = overflows && <button type="button" onClick={() => setExpanded(_temp2)} className="tap-target mt-2 text-xs text-primary hover:underline">{expanded ? lessLabel : `+${hidden} ${moreLabel}`}</button>;
    $[23] = expanded;
    $[24] = hidden;
    $[25] = lessLabel;
    $[26] = moreLabel;
    $[27] = overflows;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== t3 || $[30] !== t4 || $[31] !== t5) {
    t6 = <>{t3}{t4}{t5}</>;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  return t6;
}

/**
 * One row of the tree plus its subtree. Recursive, so the nesting follows the
 * data instead of a fixed number of levels.
 */
function _temp2(v) {
  return !v;
}
function _temp() {
  return new Set();
}
function CategoryBranch(t0) {
  const $ = _c(40);
  const {
    node,
    selected,
    onToggle,
    opened,
    onToggleBranch,
    searching,
    isRTL,
    nested: t1
  } = t0;
  const nested = t1 === undefined ? false : t1;
  const {
    category,
    children
  } = node;
  let t2;
  if ($[0] !== category.slug || $[1] !== node || $[2] !== opened || $[3] !== searching || $[4] !== selected) {
    t2 = searching || hasSelectedDescendant(node, selected) || opened.has(category.slug);
    $[0] = category.slug;
    $[1] = node;
    $[2] = opened;
    $[3] = searching;
    $[4] = selected;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const open = t2;
  const contentId = `cat-branch-${category.slug}`;
  const t3 = nested ? "relative" : undefined;
  let t4;
  if ($[6] !== nested) {
    t4 = nested && <span aria-hidden="true" className="absolute top-[22px] start-0 ms-[-12px] h-px w-3 bg-border" />;
    $[6] = nested;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== category.name || $[9] !== category.slug || $[10] !== children.length || $[11] !== contentId || $[12] !== isRTL || $[13] !== onToggleBranch || $[14] !== open) {
    t5 = children.length > 0 ? <button type="button" aria-expanded={open} aria-controls={contentId} aria-label={`${open ? isRTL ? "\u0637\u064A" : "Collapse" : isRTL ? "\u062A\u0648\u0633\u064A\u0639" : "Expand"} ${category.name}`} onClick={() => onToggleBranch(category.slug)} className="tap-target-24 shrink-0 text-muted-foreground"><CaretDown aria-hidden="true" className={`h-3 w-3 transition-transform duration-200 ${open ? "" : "-rotate-90 rtl:rotate-90"}`} /></button> : <span className="w-3 shrink-0" aria-hidden="true" />;
    $[8] = category.name;
    $[9] = category.slug;
    $[10] = children.length;
    $[11] = contentId;
    $[12] = isRTL;
    $[13] = onToggleBranch;
    $[14] = open;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== category.name || $[17] !== category.selectable || $[18] !== category.slug || $[19] !== onToggle || $[20] !== selected) {
    t6 = category.selectable ? <CheckboxRow checked={selected.includes(category.slug)} onToggle={() => onToggle(category.slug)} label={category.name} /> : <span className="flex-1 truncate py-3 text-sm text-muted-foreground">{category.name}</span>;
    $[16] = category.name;
    $[17] = category.selectable;
    $[18] = category.slug;
    $[19] = onToggle;
    $[20] = selected;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t5 || $[23] !== t6) {
    t7 = <div className="flex items-center gap-1">{t5}{t6}</div>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== children || $[26] !== contentId || $[27] !== isRTL || $[28] !== onToggle || $[29] !== onToggleBranch || $[30] !== open || $[31] !== opened || $[32] !== searching || $[33] !== selected) {
    t8 = children.length > 0 && open && <ul id={contentId} className="ms-3 flex flex-col gap-0.5 border-s border-border ps-3">{children.map(child => <CategoryBranch key={child.category.slug} node={child} selected={selected} onToggle={onToggle} opened={opened} onToggleBranch={onToggleBranch} searching={searching} isRTL={isRTL} nested={true} />)}</ul>;
    $[25] = children;
    $[26] = contentId;
    $[27] = isRTL;
    $[28] = onToggle;
    $[29] = onToggleBranch;
    $[30] = open;
    $[31] = opened;
    $[32] = searching;
    $[33] = selected;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t3 || $[36] !== t4 || $[37] !== t7 || $[38] !== t8) {
    t9 = <li className={t3}>{t4}{t7}{t8}</li>;
    $[35] = t3;
    $[36] = t4;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
}

// ─── Header (rendered inside the card so it aligns with the toolbar) ─

function FilterHeader(t0) {
  const $ = _c(10);
  const {
    anyApplied,
    onClearAll,
    isRTL,
    embedded
  } = t0;
  if (embedded && !anyApplied) {
    return null;
  }
  let t1;
  if ($[0] !== embedded || $[1] !== isRTL) {
    t1 = embedded ? <span /> : <h2 className="text-sm font-semibold text-foreground">{isRTL ? "\u0627\u0644\u0641\u0644\u0627\u062A\u0631" : "Filter"}</h2>;
    $[0] = embedded;
    $[1] = isRTL;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== anyApplied || $[4] !== isRTL || $[5] !== onClearAll) {
    t2 = anyApplied && <button type="button" onClick={onClearAll} className="tap-target text-xs text-primary hover:underline">{isRTL ? "\u0645\u0633\u062D \u0627\u0644\u0643\u0644" : "Clear All"}</button>;
    $[3] = anyApplied;
    $[4] = isRTL;
    $[5] = onClearAll;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className="flex items-center justify-between px-4 py-3">{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

// ─── Section primitive ─────────────────────────────────────────────

function FilterSection(t0) {
  const $ = _c(17);
  const {
    title,
    defaultOpen,
    children
  } = t0;
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(_temp3);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <span>{title}</span>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `h-4 w-4 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`;
  let t4;
  if ($[3] !== t3) {
    t4 = <CaretDown aria-hidden="true" className={t3} />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== contentId || $[6] !== open || $[7] !== t2 || $[8] !== t4) {
    t5 = <h3><button type="button" onClick={t1} aria-expanded={open} aria-controls={contentId} className="flex w-full items-center justify-between gap-2 text-start text-sm font-semibold text-foreground">{t2}{t4}</button></h3>;
    $[5] = contentId;
    $[6] = open;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== children || $[11] !== contentId || $[12] !== open) {
    t6 = open && <div id={contentId} className="mt-3">{children}</div>;
    $[10] = children;
    $[11] = contentId;
    $[12] = open;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <section className="px-4 py-4">{t5}{t6}</section>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

/**
 * A short list is faster to scan than to type into, so the box only appears
 * once a section is long enough to be annoying. Same threshold as show-more.
 */
function _temp3(o) {
  return !o;
}
function isSearchable(count: number): boolean {
  return count > COLLAPSED_COUNT;
}
function FilterSearch(t0) {
  const $ = _c(7);
  const {
    value,
    onChange,
    isRTL
  } = t0;
  const label = isRTL ? "\u0628\u062D\u062B" : "Search";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <MagnifyingGlass aria-hidden="true" className="pointer-events-none absolute top-1/2 start-2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onChange) {
    t2 = e => onChange(e.target.value);
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label || $[4] !== t2 || $[5] !== value) {
    t3 = <div className="relative mb-2">{t1}<input type="search" value={value} onChange={t2} placeholder={label} aria-label={label} className="w-full rounded-lg border border-border bg-background py-1.5 ps-7 pe-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" /></div>;
    $[3] = label;
    $[4] = t2;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/** Shown when a search excludes everything, so the section is never blank. */
function NoMatches(t0) {
  const $ = _c(2);
  const {
    isRTL
  } = t0;
  const t1 = isRTL ? "\u0644\u0627 \u0646\u062A\u0627\u0626\u062C" : "No matches";
  let t2;
  if ($[0] !== t1) {
    t2 = <p className="py-1 text-xs text-muted-foreground">{t1}</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}

// ─── Checkbox primitive ─────────────────────────────────────────────

function CheckboxRow(t0) {
  const $ = _c(13);
  const {
    checked,
    onToggle,
    label
  } = t0;
  const t1 = checked ? "bg-primary border-primary text-primary-foreground" : "border-border bg-background";
  let t2;
  if ($[0] !== t1) {
    t2 = ["h-4 w-4 rounded border flex items-center justify-center shrink-0 transition-colors", t1];
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = t2.join(" ");
  let t4;
  if ($[2] !== checked) {
    t4 = checked && <Check size={10} weight="bold" />;
    $[2] = checked;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <span className={t3} aria-hidden="true">{t4}</span>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== label) {
    t6 = <span className="flex-1 truncate">{label}</span>;
    $[7] = label;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== onToggle || $[10] !== t5 || $[11] !== t6) {
    t7 = <button type="button" onClick={onToggle} className="flex items-center gap-2.5 w-full py-3 text-sm text-start text-foreground hover:text-primary transition-colors">{t5}{t6}</button>;
    $[9] = onToggle;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
function CheckboxList(t0) {
  const $ = _c(33);
  const {
    items,
    selected,
    onToggle,
    moreLabel,
    lessLabel,
    isRTL
  } = t0;
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  let t1;
  if ($[0] !== items.length) {
    t1 = isSearchable(items.length);
    $[0] = items.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const searchable = t1;
  let t2;
  if ($[2] !== query || $[3] !== searchable) {
    t2 = searchable && query.trim() !== "";
    $[2] = query;
    $[3] = searchable;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const searching = t2;
  let t3;
  if ($[5] !== items || $[6] !== query || $[7] !== searching) {
    t3 = searching ? items.filter(i => matchesQuery(i.label, query)) : items;
    $[5] = items;
    $[6] = query;
    $[7] = searching;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const matched = t3;
  const overflows = !searching && matched.length > COLLAPSED_COUNT;
  let t4;
  if ($[9] !== expanded || $[10] !== matched || $[11] !== overflows) {
    t4 = expanded || !overflows ? matched : matched.slice(0, COLLAPSED_COUNT);
    $[9] = expanded;
    $[10] = matched;
    $[11] = overflows;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const visible = t4;
  const hidden = overflows ? matched.length - COLLAPSED_COUNT : 0;
  let t5;
  if ($[13] !== isRTL || $[14] !== query || $[15] !== searchable) {
    t5 = searchable && <FilterSearch value={query} onChange={setQuery} isRTL={isRTL} />;
    $[13] = isRTL;
    $[14] = query;
    $[15] = searchable;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== isRTL || $[18] !== matched.length || $[19] !== onToggle || $[20] !== selected || $[21] !== visible) {
    t6 = matched.length === 0 ? <NoMatches isRTL={isRTL} /> : <ul className="flex flex-col gap-0.5">{visible.map(item => <li key={item.key}><CheckboxRow checked={selected.includes(item.key)} onToggle={() => onToggle(item.key)} label={item.label} /></li>)}</ul>;
    $[17] = isRTL;
    $[18] = matched.length;
    $[19] = onToggle;
    $[20] = selected;
    $[21] = visible;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== expanded || $[24] !== hidden || $[25] !== lessLabel || $[26] !== moreLabel || $[27] !== overflows) {
    t7 = overflows && <button type="button" onClick={() => setExpanded(_temp4)} className="tap-target mt-2 text-xs text-primary hover:underline">{expanded ? lessLabel : `+${hidden} ${moreLabel}`}</button>;
    $[23] = expanded;
    $[24] = hidden;
    $[25] = lessLabel;
    $[26] = moreLabel;
    $[27] = overflows;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== t5 || $[30] !== t6 || $[31] !== t7) {
    t8 = <>{t5}{t6}{t7}</>;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  return t8;
}

// ─── In-stock single-toggle row ─────────────────────────────────────
function _temp4(v) {
  return !v;
}
function InStockCheckbox(t0) {
  const $ = _c(4);
  const {
    active,
    onToggle,
    isRTL
  } = t0;
  const t1 = isRTL ? "\u0645\u062A\u0648\u0641\u0631 \u0641\u0642\u0637" : "In stock only";
  let t2;
  if ($[0] !== active || $[1] !== onToggle || $[2] !== t1) {
    t2 = <CheckboxRow checked={active} onToggle={onToggle} label={t1} />;
    $[0] = active;
    $[1] = onToggle;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

// ─── Price range ────────────────────────────────────────────────────

function PriceRange(t0) {
  const $ = _c(36);
  const {
    range,
    minPrice,
    maxPrice,
    onApply,
    isRTL
  } = t0;
  const {
    currency
  } = useCurrency();
  const floor = Math.floor(range.min);
  const ceil = Math.ceil(range.max);
  const t1 = minPrice ?? floor;
  const t2 = maxPrice ?? ceil;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = [t1, t2];
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [local, setLocal] = useState(t3);
  let t4;
  let t5;
  if ($[3] !== ceil || $[4] !== floor || $[5] !== maxPrice || $[6] !== minPrice) {
    t4 = () => {
      setLocal([minPrice ?? floor, maxPrice ?? ceil]);
    };
    t5 = [minPrice, maxPrice, floor, ceil];
    $[3] = ceil;
    $[4] = floor;
    $[5] = maxPrice;
    $[6] = minPrice;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== ceil || $[10] !== floor || $[11] !== onApply) {
    t6 = next => {
      const isAtFloor = next[0] <= floor;
      const isAtCeil = next[1] >= ceil;
      onApply(isAtFloor ? null : next[0], isAtCeil ? null : next[1]);
    };
    $[9] = ceil;
    $[10] = floor;
    $[11] = onApply;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleCommit = t6;
  const t7 = isRTL ? "\u0627\u0644\u0633\u0639\u0631 \u0627\u0644\u0623\u062F\u0646\u0649" : "Minimum price";
  const t8 = isRTL ? "\u0627\u0644\u0633\u0639\u0631 \u0627\u0644\u0623\u0642\u0635\u0649" : "Maximum price";
  let t9;
  if ($[13] !== ceil || $[14] !== floor || $[15] !== handleCommit || $[16] !== local || $[17] !== t7 || $[18] !== t8) {
    t9 = <RangeSlider min={floor} max={ceil} step={1} value={local} onChange={setLocal} onCommit={handleCommit} ariaLabelStart={t7} ariaLabelEnd={t8} />;
    $[13] = ceil;
    $[14] = floor;
    $[15] = handleCommit;
    $[16] = local;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== currency || $[21] !== local[0]) {
    t10 = formatPrice(local[0], currency);
    $[20] = currency;
    $[21] = local[0];
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <span>{t10}</span>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== currency || $[26] !== local[1]) {
    t12 = formatPrice(local[1], currency);
    $[25] = currency;
    $[26] = local[1];
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <span>{t12}</span>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t11 || $[31] !== t13) {
    t14 = <div className="flex items-center justify-between text-xs text-foreground tabular-nums">{t11}{t13}</div>;
    $[30] = t11;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14 || $[34] !== t9) {
    t15 = <div className="flex flex-col gap-3">{t9}{t14}</div>;
    $[33] = t14;
    $[34] = t9;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
}

// ─── Color swatch grid ──────────────────────────────────────────────

function ColorSwatchGrid(t0) {
  const $ = _c(23);
  const {
    colors,
    selected,
    onToggle,
    isRTL,
    moreLabel,
    lessLabel
  } = t0;
  const [expanded, setExpanded] = useState(false);
  const overflows = colors.length > 12;
  let t1;
  if ($[0] !== colors || $[1] !== expanded || $[2] !== overflows) {
    t1 = expanded || !overflows ? colors : colors.slice(0, 12);
    $[0] = colors;
    $[1] = expanded;
    $[2] = overflows;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const visible = t1;
  let t2;
  if ($[4] !== isRTL || $[5] !== onToggle || $[6] !== selected || $[7] !== visible) {
    let t3;
    if ($[9] !== isRTL || $[10] !== onToggle || $[11] !== selected) {
      t3 = color => {
        const urlKey = color.value_en || color.value_ar;
        const isSelected = selected.includes(urlKey);
        const label = (isRTL ? color.value_ar : color.value_en) || color.value_ar;
        if (color.hex) {
          return <button key={urlKey} type="button" onClick={() => onToggle(urlKey)} title={label} aria-label={label} aria-pressed={isSelected} className={["tap-target h-7 w-7 rounded-full transition-all", isSelected ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "ring-1 ring-border hover:ring-foreground/40"].join(" ")} style={{
            backgroundColor: color.hex
          }} />;
        }
        return <button key={urlKey} type="button" onClick={() => onToggle(urlKey)} aria-pressed={isSelected} className={["tap-target h-7 px-3 rounded-full text-xs font-medium transition-all border", isSelected ? "bg-primary text-primary-foreground border-primary" : "bg-subtle text-foreground border-border hover:border-foreground/40"].join(" ")}>{label}</button>;
      };
      $[9] = isRTL;
      $[10] = onToggle;
      $[11] = selected;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    t2 = visible.map(t3);
    $[4] = isRTL;
    $[5] = onToggle;
    $[6] = selected;
    $[7] = visible;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[13] !== t2) {
    t3 = <div className="tap-grid flex flex-wrap gap-2">{t2}</div>;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== expanded || $[16] !== lessLabel || $[17] !== moreLabel || $[18] !== overflows) {
    t4 = overflows && <button type="button" onClick={() => setExpanded(_temp5)} className="tap-target mt-2 text-xs text-primary hover:underline">{expanded ? lessLabel : moreLabel}</button>;
    $[15] = expanded;
    $[16] = lessLabel;
    $[17] = moreLabel;
    $[18] = overflows;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== t3 || $[21] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}

// Re-export the brand DTO so consumers can type filter props
function _temp5(v) {
  return !v;
}
export type { FacetBrandDTO };